/**
 * The song page's side of the song tabs (SongScreen calls it once; it lives across song switches):
 *  - which tab the page shows: a list's tab when the URL has ?c= and the list (as this user gets it)
 *    has the song, else the song's own tab; none for a song that is not found / not visible;
 *  - marks it used (adds it on the right the first time) on every navigation;
 *  - restores the tab's sheet and page, ONLY when the strip switched to it (history state
 *    TAB_NAV_STATE) — setlist prev / next and opening from the library start at page 1;
 *  - remembers the sheet shown and the page read (SheetViewerEnv.onPage);
 *  - drops tabs the user can no longer see: a song the server said is gone at once (forgetSong),
 *    and songs / lists missing from a list fetched this session (prune).
 *
 * It never subscribes to the tabs doc itself (the strip does): a page turn must not re-render the page.
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router';
import { useCollection, useCollections, useSongs } from '../api/library';
import { orderedEntries, resolveSetlistIndex, type SetlistContext } from '../viewer/setlist';
import { findTab, forgetSong, keyOf, prune, setPage, setSheet, sheetOf, visit, type PageMemo, type TabKey, type VisitTarget } from './model';
import { getTabsState, loadTabs, SESSION_START, updateTabs } from './store';

/** History state of a navigation made by the tab strip (the tab's sheet and page are restored). */
export const TAB_NAV_STATE = { kgrTab: true } as const;

export function isTabNavigation(state: unknown): boolean {
  return !!state && typeof state === 'object' && (state as { kgrTab?: unknown }).kgrTab === true;
}

export interface TabSync {
  userId: string;
  /** The tab of the page showing; null = none (song not found, its list not known (yet)). */
  activeKey: TabKey | null;
  /** The open list entry when the page shows a list's tab (for the list drawer). */
  list: { collectionId: string; entryId: string; songId: string } | null;
  /** Strip switch: the sheet the tab showed last for this song (seeds the sheet choice); else null. */
  initialSheetId: string | null;
  /** SheetViewerEnv.initialPage: strip switch → the page the tab was left at, for that file only. */
  initialPage: (fileId: string) => number | null;
  /** SheetViewerEnv.onPage of `songId`'s page: remembered in the tab. */
  onPage: (songId: string, fileId: string, rev: number, page: number) => void;
  /** The page shows sheet `sheetId` of `songId` (the tab's second line; restored on a strip switch). */
  onSheetShown: (songId: string, sheetId: string) => void;
}

export function useTabSync({
  userId,
  songId,
  setlist,
  found,
  gone,
}: {
  userId: string;
  songId: string | undefined;
  setlist: SetlistContext | null;
  /** The song's details are here (server, cache or offline manifest). */
  found: boolean;
  /** The server said the song is gone (403 / 404). */
  gone: boolean;
}): TabSync {
  const loc = useLocation();
  const fromStrip = isTabNavigation(loc.state);
  const col = useCollection(setlist?.collectionId);
  const songsQ = useSongs();
  const colsQ = useCollections();

  // First, so the effects below (queued until the stored doc is read) find the user's tabs.
  useEffect(() => {
    loadTabs(userId);
  }, [userId]);

  const colData = setlist && col.data?.id === setlist.collectionId ? col.data : null;
  const target = useMemo<VisitTarget | null>(() => {
    if (!songId) return null;
    if (setlist && colData) {
      const ordered = orderedEntries(colData.entries);
      const k = resolveSetlistIndex(ordered, songId, setlist.index, setlist.entryId);
      // The list's tab even while the song itself does not open (the strip stays to move on).
      if (k >= 0) return { kind: 'collection', collectionId: setlist.collectionId, entryId: ordered[k]!.entryId, songId };
    } else if (setlist && col.isPending) {
      return null; // the list is still loading: no song tab in between
    }
    return found && !gone ? { kind: 'song', songId } : null;
  }, [songId, setlist, colData, col.isPending, found, gone]);
  const activeKey = target ? keyOf(target) : null;

  // What the tab remembered when the strip switched to it (read once per page, before the page
  // reports anything new). A different user's / not yet loaded doc restores nothing.
  const snapId = `${loc.key}|${activeKey ?? ''}`;
  const restore = useMemo(() => {
    const st = getTabsState();
    const tab = fromStrip && st.doc && st.userId === userId ? findTab(st.doc, activeKey) : undefined;
    return { sheetId: tab && songId ? sheetOf(tab, songId) : null, page: tab?.page ?? null };
    // Keyed on the navigation and the tab only (snapId).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapId]);

  const live = useRef({ userId, songId, activeKey, restore });
  live.current = { userId, songId, activeKey, restore };
  /** What the page reported before its tab existed (the first visit applies it). */
  const shownSheet = useRef<{ songId: string; sheetId: string } | null>(null);
  const lastPage = useRef<{ songId: string; memo: PageMemo } | null>(null);
  // Only reports of THIS navigation count: cleared while rendering it, before the new page's own
  // effects (which run before the visit below) report again.
  const navKey = useRef(loc.key);
  if (navKey.current !== loc.key) {
    navKey.current = loc.key;
    shownSheet.current = null;
    lastPage.current = null;
  }

  const targetSig = target ? JSON.stringify(target) : '';
  useEffect(() => {
    if (!target) return;
    const key = keyOf(target);
    updateTabs(userId, (d) => {
      let doc = visit(d, target, Date.now());
      const s = shownSheet.current;
      if (s && s.songId === target.songId) doc = setSheet(doc, key, s.songId, s.sheetId);
      const p = lastPage.current;
      if (p && p.songId === target.songId) doc = setPage(doc, key, p.memo);
      return doc;
    });
    // One visit per navigation and target (targetSig stands for target).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, targetSig, loc.key]);

  // Gone (403 / 404): its tab goes at once.
  useEffect(() => {
    if (gone && songId) updateTabs(userId, (d) => forgetSong(d, songId));
  }, [userId, gone, songId]);

  // Lists fetched from the server this session: tabs of songs / lists no longer in them go.
  const songsFresh = !!songsQ.data && songsQ.dataUpdatedAt >= SESSION_START;
  const colsFresh = !!colsQ.data && colsQ.dataUpdatedAt >= SESSION_START;
  useEffect(() => {
    const songs = songsFresh && songsQ.data ? new Set(songsQ.data.map((s) => s.id)) : null;
    const collections = colsFresh && colsQ.data ? new Set(colsQ.data.map((c) => c.id)) : null;
    if (songs || collections) updateTabs(userId, (d) => prune(d, { songs, collections }));
  }, [userId, songsFresh, songsQ.data, colsFresh, colsQ.data]);

  const initialPage = useCallback((fileId: string) => {
    const p = live.current.restore.page;
    return p && p.fileId === fileId ? p.page : null;
  }, []);

  const onPage = useCallback((forSong: string, fileId: string, rev: number, page: number) => {
    const { userId: u, songId: current, activeKey: key } = live.current;
    if (forSong !== current) return; // a page that is closing
    const memo = { fileId, rev, page };
    lastPage.current = { songId: forSong, memo };
    if (key) updateTabs(u, (d) => setPage(d, key, memo));
  }, []);

  const onSheetShown = useCallback((forSong: string, sheetId: string) => {
    const { userId: u, songId: current, activeKey: key } = live.current;
    if (forSong !== current) return;
    shownSheet.current = { songId: forSong, sheetId };
    if (key) updateTabs(u, (d) => setSheet(d, key, forSong, sheetId));
  }, []);

  const list = target?.kind === 'collection' ? { collectionId: target.collectionId, entryId: target.entryId, songId: target.songId } : null;
  const listSig = list ? `${list.collectionId}|${list.entryId}|${list.songId}` : '';
  return useMemo(
    () => ({ userId, activeKey, list, initialSheetId: restore.sheetId, initialPage, onPage, onSheetShown }),
    // `list` is rebuilt every render; listSig stands for it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userId, activeKey, listSig, restore.sheetId, initialPage, onPage, onSheetShown],
  );
}
